import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getReferralStats } from '../api';
import { Copy, Gift, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export const ReferralPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getReferralStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${stats.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien de parrainage copié!');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(stats.referral_code);
    toast.success('Code de parrainage copié!');
  };

  if (!user || loading) return null;

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Programme de Parrainage</h1>
          <p className="text-muted-foreground">Invitez vos amis et gagnez des points!</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-accent mb-2">
              <Gift className="w-5 h-5" />
              <span className="text-sm font-medium">Points</span>
            </div>
            <p className="text-3xl font-bold">{stats?.current_points || 0}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-primary mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium">Parrainages</span>
            </div>
            <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-green-500 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">Points gagnés</span>
            </div>
            <p className="text-3xl font-bold">{stats?.total_points || 0}</p>
          </div>
        </div>

        {/* Referral Code */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Votre code de parrainage</h2>
          
          <div className="bg-accent/10 border-2 border-accent border-dashed rounded-lg p-6 mb-4">
            <div className="text-center mb-4">
              <p className="text-sm text-muted-foreground mb-2">Code de parrainage</p>
              <p className="text-4xl font-bold text-accent tracking-wider">{stats?.referral_code}</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={copyReferralCode}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:opacity-90 transition-opacity"
              >
                <Copy className="w-4 h-4" />
                Copier le code
              </button>
              <button
                onClick={copyReferralLink}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
              >
                <Copy className="w-4 h-4" />
                Copier le lien
              </button>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <h3 className="font-semibold mb-2">Comment ça marche?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Partagez votre code ou lien de parrainage avec vos amis</li>
              <li>• Quand quelqu'un s'inscrit avec votre code, vous gagnez 10 points</li>
              <li>• Accumulez des points pour débloquer des récompenses</li>
              <li>• Plus vous parrainez, plus vous gagnez!</li>
            </ul>
          </div>
        </div>

        {/* Recent Referrals */}
        {stats?.recent_referrals && stats.recent_referrals.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">Parrainages récents</h2>
            <div className="space-y-3">
              {stats.recent_referrals.map((ref, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div>
                    <p className="font-medium">{ref.username}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(ref.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-accent">+10 points</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
